import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Upload, Button, Icon, Progress } from 'antd';
import classNames from 'class-names';
import { sortBy } from 'lodash';
import pdfSrc from '../../static/pdf-thumb.svg';
import crossSrc from '../../static/cross-circle.svg';
import styles from './ImageUploads.module.less';

const statuses = {
  DONE: 'done',
  ERROR: 'error',
};

const { Dragger } = Upload;

const getThumbUrl = ({ fileType, hasError }) => {
  if (hasError) {
    return crossSrc;
  }

  if (fileType.includes('pdf')) {
    return pdfSrc;
  }

  return undefined;
};

const ImageUploads = ({
  draggable,
  name: componentName,
  maxFilesNumber,
  onFilesSubmitted,
  onValidateFile,
  onChange,
  addFileLabel,
  uploadNowLabel,
  uploadingLabel,
  readOnlyMode,
  addSubsequentFileLabel,
  isThumbVisible,
  fileList: filesListInitialState,
}) => {
  const [fileList, setFileList] = useState(
    filesListInitialState.map(({ fileName, dataURL: url }) => ({
      uid: fileName,
      name: fileName,
      status: statuses.DONE,
      url,
    })),
  );

  const [isUploading, setUploading] = useState(false);
  const [isFinished, setFinished] = useState(false);
  const [progressUpload, setProgress] = useState(0);
  const [errorList, setErrorList] = useState([]);
  const [generalErrorList, setGeneralErrorList] = useState([]);

  const addGeneralError = (err) => {
    if (generalErrorList.find((generalErrorText) => generalErrorText === err)) {
      return;
    }

    setGeneralErrorList([...generalErrorList, err]);
  };

  const onUpdateErrors = () => {
    if (fileList && fileList.length > 0) {
      setErrorList(Array.from(new Set(fileList.map(({ hasError }) => hasError).filter((error) => error))));
      return;
    }

    setErrorList([]);
  };

  const showUploadList = {
    showPreviewIcon: true,
    showRemoveIcon: !isFinished && !readOnlyMode,
    showDownloadIcon: false,
  };

  const onFilesSubmittedHandler = () => {
    const publicList = fileList.map(({ name, originalFile }) => ({ name, content: originalFile, type: originalFile.type }));

    onFilesSubmitted(publicList, {
      onStartUploading: () => {
        setUploading(true);
      },

      onProgress: ({ percentage }) => {
        setProgress(Number.parseInt(percentage, 10));
      },

      onFinishUploading: () => {
        // this will give the user the check icon
        setTimeout(() => {
          setUploading(false);
          setFinished(true);
        }, 1000);
      },

      onErrorUploading: () => {
        setUploading(false);
      },
    });
  };

  const onUpload = async (newFile) => {
    if (fileList.find(({ name }) => newFile.name === name)) {
      // skip the duplicate file
      return;
    }

    if (fileList.length + 1 > maxFilesNumber) {
      addGeneralError(`Reached the maximum of ${maxFilesNumber} files to upload.`);
      return;
    }

    if (fileList.length + 1 === maxFilesNumber) {
      addGeneralError(`Reached the maximum of ${maxFilesNumber} files to upload.`);
    }

    const hasError = await onValidateFile(newFile);
    const url = !hasError && URL.createObjectURL(newFile);

    setFileList([
      {
        uid: newFile.uid,
        name: newFile.name,
        status: hasError ? statuses.ERROR : statuses.DONE,
        url,
        hasError,
        response: hasError,
        originalFile: !hasError && newFile,
        trackingIndex: +new Date() * ((hasError && 2) || 1),
        thumbUrl: getThumbUrl({ hasError, fileType: newFile.type }),
      },
      ...fileList,
    ]);
  };

  const onBeforeUploadHandler = () => {
    setGeneralErrorList([]);
  };

  const onRemoveFileHandler = (fileToRemove) => {
    onUpdateErrors();
    onBeforeUploadHandler();
    const newList = fileList.filter(({ uid }) => uid !== fileToRemove.uid);
    setFileList(newList);
  };

  // this is for controlled components
  useEffect(() => {
    setFileList(
      filesListInitialState.map(({ fileName, dataURL: url }) => ({
        uid: fileName,
        name: fileName,
        status: statuses.DONE,
        url,
      })),
    );
  }, [filesListInitialState]);

  useEffect(() => {
    onUpdateErrors();
    onChange(fileList);
  }, [fileList]);

  const sortedFileList = useMemo(() => sortBy(fileList, ['trackingIndex']), [fileList]);

  const UploadComponent = (draggable && Dragger) || Upload;

  const hasAnyError = errorList.length > 0 || generalErrorList.length > 0;

  return (
    <div className={classNames(isUploading && styles.isUploading)}>
      {isUploading && (
        <div className="uploading-content">
          <div className={classNames(styles.uploadingText)}>Uploading Documents...</div>
          <Progress percent={progressUpload} />
        </div>
      )}
      <div className={styles.imageUploadsContainer}>
        <UploadComponent
          className={styles.imageUploads}
          accept="application/pdf,image/*"
          name={componentName}
          data-testid={componentName}
          showUploadList={showUploadList}
          beforeUpload={onBeforeUploadHandler}
          multiple
          action={onUpload}
          customRequest={() => {}}
          listType={isThumbVisible ? 'picture' : 'text'}
          fileList={sortedFileList}
          onRemove={onRemoveFileHandler}
        >
          {hasAnyError && (
            <div className={classNames(styles.errorsSection, 'error-section')} onClick={(e) => e.stopPropagation()}>
              {errorList.length > 0 && <div className="has-error">{errorList.map((errorText) => errorText)}</div>}
              {generalErrorList.length > 0 && <div>{generalErrorList.map((errorText) => errorText)}</div>}
            </div>
          )}

          {!draggable && !isFinished && !readOnlyMode && fileList.length <= maxFilesNumber && (
            <Button
              className={classNames(fileList.length === 0 && styles.noFilesButton)}
              disabled={fileList.length === maxFilesNumber}
            >
              <Icon type="plus" />
              {`${fileList.length === 0 ? addFileLabel : addSubsequentFileLabel}`}
            </Button>
          )}
          {draggable && (
            <>
              <p className="ant-upload-drag-icon">
                <Icon type="inbox" />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
            </>
          )}
        </UploadComponent>
        {!isFinished && !readOnlyMode && fileList.length > 0 && (
          <div className={styles.imageUploadsButton}>
            <Button type="primary" disabled={errorList.length > 0} onClick={onFilesSubmittedHandler} loading={isUploading}>
              <Icon type="upload" />
              {isUploading ? uploadingLabel : uploadNowLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

ImageUploads.propTypes = {
  draggable: PropTypes.bool,
  isThumbVisible: PropTypes.bool,
  name: PropTypes.string,
  fileList: PropTypes.array,
  addFileLabel: PropTypes.string,
  addSubsequentFileLabel: PropTypes.string,
  uploadNowLabel: PropTypes.string,
  uploadingLabel: PropTypes.string,
  onValidateFile: PropTypes.func,
  onChange: PropTypes.func,
  readOnlyMode: PropTypes.bool,
  onFilesSubmitted: PropTypes.func.isRequired,
  maxFilesNumber: PropTypes.number,
};

ImageUploads.defaultProps = {
  draggable: false,
  isThumbVisible: true,
  name: 'file-upload',
  fileList: [],
  addFileLabel: 'Add File',
  addSubsequentFileLabel: 'Add Another',
  uploadNowLabel: 'Upload Now',
  uploadingLabel: 'Uploading',
  readOnlyMode: false,
  onValidateFile: () => [],
  onChange: () => {},
  maxFilesNumber: 10,
};

export default ImageUploads;
